import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { Avatar } from './Avatar';

// Presentation only. Emits the same author ids the chip row did, so the existing
// connection-scoped filtering stays authoritative. Scales to any number of people.
export const PersonPicker = ({ people, value, onChange, idPrefix = 'filter', allowSelfOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [draft, setDraft] = useState([]);
  const searchRef = useRef(null);
  const sheetRef = useRef(null);

  const selectedIds = value || [];

  // If a selected person stops being a connection, drop them rather than
  // leaving a filter that can never match.
  useEffect(() => {
    if (!selectedIds.length || !people?.length) return;
    const live = selectedIds.filter((id) => people.some((p) => p.id === id));
    if (live.length !== selectedIds.length) onChange(live);
  }, [people, selectedIds, onChange]);

  useEffect(() => {
    if (!open) return undefined;
    setTerm('');
    const focus = setTimeout(() => searchRef.current?.focus(), 120);
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      // Keep Tab inside the dialog rather than wandering into the page behind.
      if (e.key !== 'Tab') return;
      const items = Array.from(
        sheetRef.current?.querySelectorAll('button, input') || [],
      );
      if (!items.length) return;
      e.preventDefault();
      const at = items.indexOf(document.activeElement);
      const next = e.shiftKey
        ? (at <= 0 ? items.length - 1 : at - 1)
        : (at === items.length - 1 ? 0 : at + 1);
      items[next].focus();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(focus);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const matches = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return people || [];
    return (people || []).filter(
      (p) =>
        p.label.toLowerCase().includes(needle) ||
        (p.name || '').toLowerCase().includes(needle),
    );
  }, [people, term]);

  // Two people is the default: a picker offering one person filters nothing.
  // Feed opts in with allowSelfOnly, because there "You" is how a member with
  // no connections reaches their own recommendations.
  if (!people || people.length < (allowSelfOnly ? 1 : 2)) return null;

  // Ticks are provisional until the primary action is pressed; dismissing cancels.
  const openPicker = () => {
    setDraft(selectedIds);
    setOpen(true);
  };

  const toggle = (id) =>
    setDraft((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  const selectedLabel = () => {
    if (selectedIds.length === 1) {
      const one = people.find((p) => p.id === selectedIds[0]);
      return one ? one.label : '1 person';
    }
    return `${selectedIds.length} people`;
  };

  return (
    <>
      <div
        className="chip-row"
        role="group"
        aria-label="Filter by people"
        data-testid={`${idPrefix}-people`}
      >
        <button
          type="button"
          className={`chip ${!selectedIds.length ? 'active' : ''}`}
          aria-pressed={!selectedIds.length}
          onClick={() => onChange([])}
          data-testid={`${idPrefix}-person-all`}
        >
          All people
        </button>

        {selectedIds.length ? (
          <span className="chip active chip-selected-person">
            <button
              type="button"
              className="chip-selected-label"
              onClick={openPicker}
              data-testid={`${idPrefix}-person-selected`}
            >
              {selectedLabel()}
            </button>
            <button
              type="button"
              className="chip-selected-clear"
              onClick={() => onChange([])}
              aria-label="Clear selected people"
              data-testid={`${idPrefix}-person-clear`}
            >
              <X />
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="chip"
            onClick={openPicker}
            aria-haspopup="dialog"
            data-testid={`${idPrefix}-choose-person`}
          >
            Choose people
            <ChevronDown className="chip-caret" aria-hidden="true" />
          </button>
        )}
      </div>

      {open && (
        <div
          className="person-sheet-overlay"
          onClick={() => setOpen(false)}
          data-testid={`${idPrefix}-person-sheet-overlay`}
        >
          <div
            className="person-sheet"
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-label="Recommendations from"
            onClick={(e) => e.stopPropagation()}
            data-testid={`${idPrefix}-person-sheet`}
          >
            <div className="person-sheet-head">
              <h2 className="person-sheet-title">Recommendations from</h2>
              <button
                type="button"
                className="person-sheet-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid={`${idPrefix}-person-sheet-close`}
              >
                <X />
              </button>
            </div>

            <div className="person-search">
              <Search className="person-search-icon" aria-hidden="true" />
              <input
                ref={searchRef}
                className="person-search-input"
                type="text"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Search people"
                aria-label="Search people"
                data-testid={`${idPrefix}-person-search`}
              />
            </div>

            <div className="person-list" data-testid={`${idPrefix}-person-list`}>
              {matches.length === 0 ? (
                <p className="person-list-empty" data-testid={`${idPrefix}-person-no-results`}>
                  No one matches that name.
                </p>
              ) : (
                matches.map((person) => {
                  const ticked = draft.includes(person.id);
                  return (
                    <button
                      key={person.id}
                      type="button"
                      className={`person-row ${ticked ? 'active' : ''}`}
                      role="checkbox"
                      aria-checked={ticked}
                      onClick={() => toggle(person.id)}
                      data-testid={`${idPrefix}-person-${person.id}`}
                    >
                      <Avatar
                        userId={person.id}
                        name={person.name}
                        className="person-row-avatar"
                        testId={`${idPrefix}-avatar-${person.id}`}
                      />
                      <span className="person-row-name">{person.label}</span>
                      <span className={`person-row-tick ${ticked ? 'on' : ''}`} aria-hidden="true">
                        {ticked && <Check />}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <button
              type="button"
              className="person-sheet-apply"
              onClick={apply}
              data-testid={`${idPrefix}-person-apply`}
            >
              {draft.length
                ? `Show recommendations (${draft.length})`
                : 'Show all recommendations'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
